import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/typed";

const SAME = "";
const sel = (id: string, label: string, value: string, set: (v: string) => void, opts: [string, string][]) => (
  <label className="block"><span className="field-label">{label}</span>
    <select className="field-input" value={value} onChange={(e) => set(e.target.value)} data-testid={`bulk-org-${id}`}>
      <option value={SAME}>Leave unchanged</option>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

export function BulkOrgEdit({ ids, open, onOpenChange, onDone }: { ids: string[]; open: boolean; onOpenChange: (o: boolean) => void; onDone: () => void }) {
  const [form, setForm] = useState(SAME);
  const [brand, setBrand] = useState(SAME);
  const [pays, setPays] = useState(SAME);
  const [fee, setFee] = useState("");
  const [agency, setAgency] = useState("");
  const [setAg, setSetAg] = useState(false);
  const [active, setActive] = useState(SAME);
  const [county, setCounty] = useState("");
  const [civil, setCivil] = useState("");
  const [base, setBase] = useState("");
  const [baseMode, setBaseMode] = useState(SAME);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm(SAME); setBrand(SAME); setPays(SAME); setFee(""); setAgency(""); setSetAg(false); setActive(SAME); setCounty(""); setCivil(""); setBase(""); setBaseMode(SAME); } }, [open]);
  const body: any = { ids };
  if (form) body.form_type = form;
  if (brand) body.report_brand = brand;
  if (pays) body.applicant_pays = pays === "yes";
  if (pays === "yes" && fee) body.applicant_fee = Number(fee);
  if (setAg) body.agency = agency;
  if (active) body.active = active === "active";
  if (county !== "") body.county_fee = Number(county);
  if (civil !== "") body.civil_fee = Number(civil);
  if (baseMode === "custom" && base !== "") body.base_fee = Number(base);
  if (baseMode === "site") body.clear_base_fee = true;
  const any = Object.keys(body).length > 1;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/organizations/bulk-update", body); toast.success(`${data.updated} organization${data.updated === 1 ? "" : "s"} updated`); onDone(); onOpenChange(false); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="bulk-org-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Edit {ids.length} organization{ids.length === 1 ? "" : "s"}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#64748B]">Only the fields you change are updated. Everything else stays as it is on each organization.</p>
        <div className="space-y-4">
          {sel("form-type", "Application form", form, setForm, [["standard", "Standard"], ["dow", "Diocese of Wilmington"], ["nch", "New Castle Help"]])}
          {sel("brand", "Client type (sets the Airtable table)", brand, setBrand, [["vv", "Volunteer / employee screening"], ["ir", "Attorney / court client"]])}
          {sel("pays", "Applicants pay by card", pays, setPays, [["yes", "Yes"], ["no", "No"]])}
          {pays === "yes" && <label className="block"><span className="field-label">Applicant fee ($), blank keeps each organization's fee</span>
            <input type="number" min={1} step="0.01" className="field-input" value={fee} onChange={(e) => setFee(e.target.value)} data-testid="bulk-org-fee" /></label>}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" className="h-4 w-4 accent-[#1B365D]" checked={setAg} onChange={(e) => setSetAg(e.target.checked)} data-testid="bulk-org-agency-toggle" /> Set agency (internal)</label>
            {setAg && <input className="field-input mt-2" value={agency} maxLength={120} placeholder="Leave blank to clear" onChange={(e) => setAgency(e.target.value)} data-testid="bulk-org-agency" />}
          </div>
          <div className="space-y-3 rounded-xl border border-slate-200 p-3" data-testid="bulk-org-fees">
            <p className="text-sm font-semibold">Monthly bill fees <span className="font-normal text-[#64748B]">(blank leaves each organization's fee as it is, 0 turns it off)</span></p>
            {sel("base-mode", "Base search fee", baseMode, setBaseMode, [["custom", "Set a price for these organizations"], ["site", "Use the site-wide price"]])}
            {baseMode === "custom" && <input type="number" min={0} step="0.01" className="field-input" placeholder="Base fee ($)" value={base} onChange={(e) => setBase(e.target.value)} data-testid="bulk-org-base-fee" />}
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block"><span className="field-label">Fee per additional county/state ($)</span>
                <input type="number" min={0} step="0.01" className="field-input" value={county} onChange={(e) => setCounty(e.target.value)} data-testid="bulk-org-county-fee" /></label>
              <label className="block"><span className="field-label">Civil search fee ($)</span>
                <input type="number" min={0} step="0.01" className="field-input" value={civil} onChange={(e) => setCivil(e.target.value)} data-testid="bulk-org-civil-fee" /></label>
            </div>
          </div>
          {sel("active", "Status", active, setActive, [["active", "Active"], ["inactive", "Inactive"]])}
          <button className="btn-primary w-full" disabled={busy || !any} onClick={save} data-testid="bulk-org-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Apply to {ids.length} organization{ids.length === 1 ? "" : "s"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
